import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { Plus, Edit2, Trash2, X, Shield, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const initialForm = {
  name: '',
  employee_category: 'Staff',
  entitlements: { CL: 12, SL: 8, EL: 21, 'Comp Off': 6 },
  carry_forward: { EL: 30 },
  encashment_cap: { EL: 60 },
  is_active: true,
};

const LEAVE_TYPES = ['CL', 'SL', 'EL', 'Comp Off', 'Maternity', 'Paternity', 'Bereavement', 'Marriage', 'LWP'];

export default function LeavePolicies() {
  const [policies, setPolicies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [canonicalPolicy, setCanonicalPolicy] = useState(null);

  const load = async () => {
    try {
      const res = await apiGet('/hr/leave-policies');
      setPolicies(res.policies || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => {
    load();
    apiGet('/hr/leaves/policy').then(setCanonicalPolicy).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await apiPut(`/hr/leave-policies/${editing.id}`, form);
        toast.success('Policy updated');
      } else {
        await apiPost('/hr/leave-policies', form);
        toast.success('Policy created');
      }
      setShowForm(false); setEditing(null); setForm(initialForm); load();
    } catch (err) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete policy?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/hr/leave-policies/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed'); }
  };

  const updateEntitlement = (type, value) => {
    setForm({ ...form, entitlements: { ...form.entitlements, [type]: parseInt(value) || 0 } });
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Leave Policies</h1>
          <p className="text-sm text-slate-600 mt-1">Define category-wise leave entitlements, carry-forward & encashment rules.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(initialForm); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="add-policy-btn">
          <Plus size={16} /> New Policy
        </button>
      </div>

      {/* EEPC canonical policy banner (enforced in backend) */}
      {canonicalPolicy?.policy && (
        <div className="mb-6 bg-gradient-to-br from-teal-50 to-teal-50 border border-teal-200 rounded-sm p-5" data-testid="eepc-policy-banner">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} className="text-[#1F7A6E]" />
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-[#1F7A6E]">
              EEPC Leave Policy v1 · Enforced in Backend
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
            {Object.entries(canonicalPolicy.policy).map(([code, p]) => (
              <div key={code} className="bg-white/80 backdrop-blur border border-teal-100 rounded-sm p-3">
                <div className="font-display font-bold text-sm text-[#0F172A]">{p.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{code}</div>
                <div className="mt-2 text-[11px] text-slate-700">
                  {code === 'SPL' ? (
                    <>Max <strong>{p.max_per_month}</strong> × {p.block_duration_hours}h / month</>
                  ) : (
                    <>Quota <strong>{p.annual_quota}</strong> {p.annual_quota === 0 ? '· LWP' : 'days/yr'}</>
                  )}
                </div>
                <div className="mt-1 text-[10px] text-slate-500 space-y-0.5">
                  {p.probation_required && <div>· Post-probation only</div>}
                  {p.min_block_days > 1 && <div>· Min {p.min_block_days}-day block</div>}
                  {p.medical_cert_after_days && <div>· Cert req. &gt; {p.medical_cert_after_days}d</div>}
                  {p.gender && <div>· {p.gender} employees</div>}
                  {p.sandwich_rule && <div>· Sandwich rule</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-600">
            <strong>Approval matrix:</strong>{' '}
            {Object.entries(canonicalPolicy.approval_matrix || {}).map(([k, v]) => (
              <span key={k} className="mr-3"><code className="bg-white px-1.5 py-0.5 rounded text-[10px]">{k}</code> → {v}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {policies.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`policy-card-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-sm bg-teal-50 flex items-center justify-center">
                  <Shield size={15} className="text-[#1F7A6E]" />
                </div>
                <div>
                  <div className="font-display font-bold text-base text-[#0F172A]">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.employee_category}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(p); setForm({ ...initialForm, ...p }); setShowForm(true); }} className="text-slate-500 hover:text-[#1F7A6E]"><Edit2 size={14} /></button>
                <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-2">Entitlements</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(p.entitlements || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between bg-slate-50 rounded-sm px-2 py-1">
                    <span className="text-slate-600">{k}</span><span className="font-display font-bold text-[#0F172A]">{v}d</span>
                  </div>
                ))}
              </div>
            </div>

            {Object.keys(p.carry_forward || {}).length > 0 && (
              <div className="border-t border-slate-100 pt-3 mt-3">
                <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">Carry Forward</div>
                <div className="text-xs text-slate-700">{Object.entries(p.carry_forward).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>
              </div>
            )}
            {Object.keys(p.encashment_cap || {}).length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">Encashment Cap</div>
                <div className="text-xs text-slate-700">{Object.entries(p.encashment_cap).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>
              </div>
            )}
          </div>
        ))}
        {!policies.length && <div className="col-span-full p-12 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-sm">No policies defined.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editing ? 'Edit Policy' : 'New Leave Policy'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Name *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required /></div>
                <div>
                  <label className="label">Employee Category *</label>
                  <select value={form.employee_category} onChange={(e) => setForm({ ...form, employee_category: e.target.value })} className="input">
                    <option>Staff</option><option>Worker</option><option>Contract</option><option>Intern</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Annual Entitlements (days)</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {LEAVE_TYPES.map(t => (
                    <div key={t} className="flex items-center gap-2 border border-slate-200 rounded-sm px-2 py-1">
                      <span className="text-xs text-slate-600 w-16">{t}</span>
                      <input type="number" min="0" value={form.entitlements?.[t] || 0} onChange={(e) => updateEntitlement(t, e.target.value)} className="flex-1 bg-transparent text-sm focus:outline-none text-right" />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Carry Forward Cap (per type)</label>
                <input type="text" value={JSON.stringify(form.carry_forward || {})} onChange={(e) => { try { setForm({ ...form, carry_forward: JSON.parse(e.target.value) }); } catch {} }} className="input font-mono text-xs" placeholder='{"EL":30}' />
              </div>
              <div>
                <label className="label">Encashment Cap</label>
                <input type="text" value={JSON.stringify(form.encashment_cap || {})} onChange={(e) => { try { setForm({ ...form, encashment_cap: JSON.parse(e.target.value) }); } catch {} }} className="input font-mono text-xs" placeholder='{"EL":60}' />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Active
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
